import { useCallback, useEffect, useMemo, useState, type DependencyList } from "react";

export type ResourceState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setData: React.Dispatch<React.SetStateAction<T | null>>;
};

export function useResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  dependencies: DependencyList,
  fallbackError: string
): ResourceState<T> {
  // Tag results during render: an effect-only reset leaves one interactive render
  // containing the old resource after the caller has selected a new identity.
  const scope = useMemo(() => ({}), dependencies);
  const [result, setResult] = useState<{ scope: object; data: T | null; loading: boolean; error: string | null }>(
    { scope, data: null, loading: true, error: null }
  );
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  const setData: ResourceState<T>["setData"] = (next) => setResult((current) => current.scope !== scope ? current : ({
    scope,
    data: typeof next === "function" ? (next as (previous: T | null) => T | null)(current.scope === scope ? current.data : null) : next,
    loading: current.scope === scope ? current.loading : false,
    error: null
  }));

  useEffect(() => {
    const controller = new AbortController();
    let ignore = false;
    setResult((current) => ({ scope, data: current.scope === scope ? current.data : null, loading: true, error: null }));
    void loader(controller.signal)
      .then((data) => {
        if (!ignore) setResult({ scope, data, loading: false, error: null });
      })
      .catch((reason: unknown) => {
        if (ignore || (reason instanceof DOMException && reason.name === "AbortError")) return;
        setResult({ scope, data: null, loading: false, error: reason instanceof Error ? reason.message : fallbackError });
      });
    return () => {
      ignore = true;
      controller.abort();
    };
    // The scope changes only when the caller's semantic dependencies change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, revision]);

  return {
    data: result.scope === scope ? result.data : null,
    loading: result.scope !== scope || result.loading,
    error: result.scope === scope ? result.error : null,
    reload,
    setData
  };
}

export function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
}
